import React from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useForgotPassword } from '@/hooks/use-api';
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from '@/lib/validations';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import logo from '/onextel-logo-white.png';

const ForgotPasswordPage: React.FC = () => {
  const forgotPasswordMutation = useForgotPassword();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema as any),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordFormData) => {
    if (isSubmitting) return;
    toast.dismiss('forgot-toast');
    setIsSubmitting(true);
    try {
      await forgotPasswordMutation.mutateAsync({ email: values.email });
    } catch (error: any) {
      console.error('Forgot password error:', error);
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          form.setError(err.field as keyof ForgotPasswordFormData, {
            message: err.message,
          });
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="login-bg-container">
      <section className="dark:bg-main-dark-bg relative">
        <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
          <div className="w-full md:mt-0 max-w-screen-lg">
            <Card className="glassy-card flex flex-row w-full">
              {/* Left branding panel */}
              <div className="w-full md:w-1/2 hidden md:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-red-500 to-red-600 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-400 rounded-full opacity-20 -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-red-400 rounded-full opacity-20 -ml-12 -mb-12"></div>
                <div className="flex flex-col justify-center items-center text-center z-10 max-w-sm">
                  <img className="w-32 mb-8" src={logo} alt="onextel-logo" />
                  <h1 className="text-white font-bold text-3xl mb-4">
                    Never lose access again.
                  </h1>
                  <p className="text-white/90 text-sm mb-8 leading-relaxed">
                    Quick and secure password recovery to get you back to your
                    SMS campaigns in no time.
                  </p>
                  <div className="space-y-4 w-full">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <span className="text-sm">Instant email delivery</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">Secure recovery link</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">Verified account access</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right form panel */}
              <div className="w-full md:w-1/2">
                <div className="auth-form-content">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Reset Password
                    </h2>
                    <p className="text-sm text-gray-600">
                      Enter your email to receive reset instructions
                    </p>
                  </div>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">Email</FormLabel>
                            <FormControl>
                              <Input placeholder="you@company.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        className="w-full bg-red-500 hover:bg-[#23203f] text-white font-medium"
                        type="submit"
                        disabled={
                          forgotPasswordMutation.isPending || isSubmitting
                        }
                      >
                        {forgotPasswordMutation.isPending || isSubmitting
                          ? 'Sending...'
                          : 'Send Reset Link'}
                      </Button>
                      <div className="auth-divider">or</div>
                      <div className="flex flex-col space-y-3">
                        <Link className="w-full" to="/login">
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-600 hover:bg-[#23203f] hover:text-white hover:border-[#23203f] transition-colors"
                          >
                            Back to Login
                          </Button>
                        </Link>
                        <Link className="w-full" to="/register">
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-600 hover:bg-[#23203f] hover:text-white hover:border-[#23203f] transition-colors"
                          >
                            Create Account
                          </Button>
                        </Link>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ForgotPasswordPage;
